/**
 * Pure data shaping for the inline table card's Copy and Save-as-CSV actions.
 *
 * Separate from the component so the escaping rules can be tested directly —
 * these tables can carry trading-partner text straight off an EDI document, so
 * what happens to a cell containing a tab, a quote, or a leading `=` matters.
 */

/**
 * Stop a spreadsheet from executing a cell value.
 *
 * Excel and Sheets evaluate any cell beginning `=`, `+`, `-`, `@`, or a leading
 * tab/CR as a formula, which is how an exported EDI description turns into code
 * execution on the machine that opens it. Prefixing an apostrophe makes the cell
 * literal text and shows only in the formula bar. Mirrors the same guard on the
 * backend's CSV export (`neutralizeSpreadsheetFormula` in `OrderfulApiService`).
 */
export function neutralizeSpreadsheetFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/**
 * Column labels safe to use as object keys, since `downloadCSV` keys rows by
 * label and a model can easily emit the same header twice — which would silently
 * drop a column.
 */
export function uniqueColumnKeys(columns: string[]): string[] {
  // Track the keys actually emitted, not the counts of original labels: with
  // headers like ['A', 'A', 'A (2)'] a count-based suffix generates 'A (2)'
  // twice, and `Object.fromEntries` then drops a column silently.
  const used = new Set<string>();
  return columns.map((column, index) => {
    const label = column.trim() || `Column ${index + 1}`;
    let key = label;
    let suffix = 2;
    while (used.has(key)) {
      key = `${label} (${suffix})`;
      suffix += 1;
    }
    used.add(key);
    return key;
  });
}

/**
 * Row arrays to the label-keyed records `downloadCSV` expects. Cells beyond the
 * header set are dropped and missing ones become empty, so a ragged spec still
 * exports something sane.
 */
export function toCsvRecords(
  columns: string[],
  rows: string[][],
): Array<Record<string, string>> {
  // Headers are neutralized too: Papa writes them as the first CSV line, so a
  // header beginning `=` is just as executable as a cell.
  //
  // Neutralize *before* uniquifying, not after. Neutralizing afterwards can
  // create the very collision `uniqueColumnKeys` exists to prevent: headers
  // ['=A', "'=A"] are distinct going in, and both become "'=A" coming out, so
  // `Object.fromEntries` drops a column.
  const keys = uniqueColumnKeys(columns.map(neutralizeSpreadsheetFormula));
  return rows.map((row) =>
    Object.fromEntries(
      keys.map((key, index) => [
        key,
        neutralizeSpreadsheetFormula(row[index] ?? ''),
      ]),
    ),
  );
}

/**
 * Tab-separated text for the clipboard: pastes as real columns into Excel,
 * Sheets, and Slack, which is what someone copying a PO list is about to do.
 * Cells have tabs and newlines collapsed so a stray one can't shift the grid.
 */
export function toClipboardText(columns: string[], rows: string[][]): string {
  // Flatten first, then neutralize: a cell like "\n=SUM(A1)" only reveals its
  // leading `=` once the whitespace is stripped. Pasting into a spreadsheet
  // evaluates formulas exactly as opening a CSV does, so the clipboard needs the
  // same guard as the download — headers included.
  const clean = (cell: string) =>
    neutralizeSpreadsheetFormula(cell.replace(/[\t\r\n]+/g, ' ').trim());
  return [columns, ...rows].map((row) => row.map(clean).join('\t')).join('\n');
}
